import React, { useState, useEffect, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import Confetti from 'react-confetti';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CurrentBidOverlay = () => {
  const [auctionData, setAuctionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overlayEvent, setOverlayEvent] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const socketRef = useRef(null);
  const overlayTimeoutRef = useRef(null);

  const urlParams = new URLSearchParams(window.location.search);
  const inviteCode = urlParams.get('i');

  useEffect(() => {
    if (!inviteCode) {
      setLoading(false);
      return;
    }

    fetchAuctionData();
    
    // WebSocket connection for real-time updates
    console.log('[DEBUG] Creating WebSocket connection to wss://auctionsnew.4cr.in');
    const socket = new WebSocket('wss://auctionsnew.4cr.in');
    
    socketRef.current = socket;
    
    socket.onopen = () => {
      console.log('[DEBUG] WebSocket connected successfully');
      
      // Join auction room
      socket.send(JSON.stringify({
        type: 'joinAuction',
        auctionId: inviteCode
      }));
      console.log('[DEBUG] Joined auction room:', inviteCode);
    };
    
    socket.onclose = (event) => {
      console.log('[DEBUG] WebSocket disconnected:', event.code, event.reason);
    };
    
    socket.onerror = (error) => {
      console.error('[DEBUG] WebSocket error:', error);
    };
    
    socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log('[DEBUG] Received message:', message);
        
        if (message.type === 'auctionEvent') {
          handleAuctionEvent(message);
        }
      } catch (error) {
        console.error('[DEBUG] Error parsing WebSocket message:', error);
      }
    };
    
    return () => {
      console.log('[DEBUG] Cleaning up: closing WebSocket connection');
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        socket.close();
      }
      if (overlayTimeoutRef.current) {
        clearTimeout(overlayTimeoutRef.current);
      }
    };
  }, [inviteCode]);

  const fetchAuctionData = async () => {
    try {
      if (!inviteCode) return;

      const response = await fetch(global.config.app.backend_api_url + 'auction/get_current_auction_status_by_invite_code/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          invite_code: inviteCode
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAuctionData(data);
    } catch (error) {
      console.error('Error fetching auction data:', error);
    } finally {
      setLoading(false);
    }
  };

  const scheduleOverlayReset = (delay = 5000) => {
    if (overlayTimeoutRef.current) {
      clearTimeout(overlayTimeoutRef.current);
    }
    overlayTimeoutRef.current = setTimeout(() => {
      setOverlayEvent(null);
      overlayTimeoutRef.current = null;
    }, delay);
  };

  const handleAuctionEvent = (message) => {
    console.log('[DEBUG] handleAuctionEvent called', message);
    const { eventType, data } = message;
    
    switch (eventType) {
      case 'biddingStart':
        setOverlayEvent({ type: 'bidding', data });
        setShowConfetti(false);
        if (overlayTimeoutRef.current) {
          clearTimeout(overlayTimeoutRef.current);
          overlayTimeoutRef.current = null;
        }
        break;
      case 'playerSold':
        setOverlayEvent({ type: 'sold', data });
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);
        scheduleOverlayReset(5000);
        fetchAuctionData();
        break;
      case 'Unsold':
        setOverlayEvent({ type: 'unsold', data });
        scheduleOverlayReset(5000);
        toast.warning(<UnsoldDisplay player={data} />, {
          position: "top-center",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        fetchAuctionData();
        break;
      case 'UndoSold':
        toast.info('Undo Sold', {
          position: "top-center",
          autoClose: 3000,
        });
        setOverlayEvent(null);
        if (overlayTimeoutRef.current) {
          clearTimeout(overlayTimeoutRef.current);
          overlayTimeoutRef.current = null;
        }
        fetchAuctionData();
        break;
      default:
        break;
    }
  };

  const topSell = auctionData?.auction_details?.top_sell || [];
  const recentSell = auctionData?.auction_details?.recent_sell || [];

  const renderOverlayContent = () => {
    if (!overlayEvent) return null;

    const sharedCardStyle = {
      width: 'min(1100px, calc(100vw - 48px))',
      height: '200px',
      borderRadius: '24px',
      background: 'rgba(0,0,0,0.85)',
      display: 'flex',
      alignItems: 'center',
      padding: '24px 32px',
      gap: '24px',
      border: '2px solid rgba(0,255,136,0.25)',
      boxShadow: '0 20px 60px rgba(0,0,0,0.45)',
      backdropFilter: 'blur(14px)',
      color: '#fff',
      pointerEvents: 'auto',
      overflow: 'hidden'
    };

    const player = overlayEvent.data;

    if (!player) return null;

    switch (overlayEvent.type) {
      case 'bidding':
        return (
          <div style={{ ...sharedCardStyle, animation: 'pulse 2s infinite' }}>
            <img
              src={player.img}
              alt={player.name}
              style={{
                width: '140px',
                height: '140px',
                borderRadius: '20px',
                objectFit: 'cover',
                border: '4px solid #00ff88',
                boxShadow: '0 10px 30px rgba(0,255,136,0.35)',
                background: '#f8f9fa'
              }}
              onError={e => { e.currentTarget.src = '/placeholder.svg'; }}
            />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ fontSize: '14px', letterSpacing: '4px', color: '#ff4d4f', fontWeight: 700, marginBottom: '8px' }}>LIVE BIDDING</div>
              <div style={{ fontSize: '38px', fontWeight: 800, color: '#00ff88', textTransform: 'uppercase', lineHeight: 1 }}>{player.name}</div>
              <div style={{ marginTop: '10px', display: 'flex', gap: '24px', fontSize: '18px', color: '#cfcfcf' }}>
                <span>SL #{player.pdf_sl_no}</span>
                <span>Category: {player.category || 'N/A'}</span>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '16px', color: '#9c9c9c' }}>Current Bid</div>
              <div style={{ fontSize: '42px', fontWeight: 800, color: '#ffaa00', textShadow: '0 0 12px rgba(255,170,0,0.6)' }}>
                {player.amount}
              </div>
            </div>
          </div>
        );
      case 'sold':
        return (
          <div style={{ ...sharedCardStyle, borderColor: '#00ff88', background: 'rgba(0,40,20,0.92)' }}>
            <img
              src={player.img}
              alt={player.name}
              style={{
                width: '140px',
                height: '140px',
                borderRadius: '70px',
                objectFit: 'cover',
                border: '4px solid #00ff88',
                boxShadow: '0 10px 30px rgba(0,255,136,0.35)',
                background: '#f8f9fa'
              }}
              onError={e => { e.currentTarget.src = '/placeholder.svg'; }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', letterSpacing: '4px', color: '#00ff88', fontWeight: 700, marginBottom: '12px' }}>PLAYER SOLD</div>
              <div style={{ fontSize: '40px', fontWeight: 800, textTransform: 'uppercase', color: '#fff', marginBottom: '4px' }}>{player.name}</div>
              <div style={{ fontSize: '18px', color: '#8fd3ff', fontWeight: 600 }}>{player.team}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '16px', color: '#9c9c9c' }}>Winning Bid</div>
              <div style={{ fontSize: '46px', fontWeight: 800, color: '#ffaa00' }}>{player.amount}</div>
            </div>
          </div>
        );
      case 'unsold':
        return (
          <div style={{ ...sharedCardStyle, borderColor: '#ff0064', background: 'rgba(35,0,20,0.9)' }}>
            <img
              src={player.img}
              alt={player.name}
              style={{
                width: '120px',
                height: '120px',
                borderRadius: '20px',
                objectFit: 'cover',
                border: '4px solid #ff0064',
                boxShadow: '0 10px 30px rgba(255,0,100,0.35)',
                background: '#f8f9fa'
              }}
              onError={e => { e.currentTarget.src = '/placeholder.svg'; }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', letterSpacing: '4px', color: '#ff0064', fontWeight: 700, marginBottom: '12px' }}>PLAYER UNSOLD</div>
              <div style={{ fontSize: '36px', fontWeight: 800, textTransform: 'uppercase', color: '#fff', marginBottom: '6px' }}>{player.name}</div>
              <div style={{ fontSize: '18px', color: '#ff8bb4', fontWeight: 600 }}>SL #{player.pdf_sl_no}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '16px', color: '#ff8bb4' }}></div>
              <div style={{ fontSize: '40px', fontWeight: 800, color: '#ff8bb4' }}></div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const renderTicker = () => {
    const hasTickerData = topSell.length > 0 || recentSell.length > 0;

    if (!hasTickerData || overlayEvent) return null;

    const renderTickerLoop = (loopIndex) => (
      <div
        key={`ticker-loop-${loopIndex}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '20px',
          marginRight: '40px'
        }}
      >
        {topSell.length > 0 && (
          <>
            <span style={{
              color: '#ffaa00',
              fontWeight: 700,
              fontSize: '13px',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              🏆 Top Sell
            </span>
            {topSell.map((player, index) => (
              <span key={`top-${loopIndex}-${index}`} style={{
                color: '#fff',
                fontSize: '14px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span style={{ color: '#00ff88', fontWeight: 700 }}>
                  {player.full_name || player.name || 'N/A'}
                </span>
                <span style={{ color: '#888' }}>→</span>
                <span style={{ color: '#ffaa00', fontWeight: 700 }}>
                  {player.auction_sold_amount || player.amount || '0'}
                </span>
                <span style={{ color: '#8fd3ff', fontSize: '12px' }}>
                  ({player.team_name || player.team || 'N/A'})
                </span>
                <span style={{ color: '#333' }}>•</span>
              </span>
            ))}
          </>
        )}

        {recentSell.length > 0 && (
          <>
            <span style={{
              color: '#00aaff',
              fontWeight: 700,
              fontSize: '13px',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              ⚡ Recent Sell
            </span>
            {recentSell.map((player, index) => (
              <span key={`recent-${loopIndex}-${index}`} style={{
                color: '#fff',
                fontSize: '14px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span style={{ color: '#00ff88', fontWeight: 700 }}>
                  {player.full_name || player.name || 'N/A'}
                </span>
                <span style={{ color: '#888' }}>→</span>
                <span style={{ color: '#00aaff', fontWeight: 700 }}>
                  {player.auction_sold_amount || player.amount || '0'}
                </span>
                <span style={{ color: '#8fd3ff', fontSize: '12px' }}>
                  ({player.team_name || player.team || 'N/A'})
                </span>
                <span style={{ color: '#333' }}>•</span>
              </span>
            ))}
          </>
        )}
      </div>
    );

    return (
      <div style={{
        width: 'min(1100px, calc(100vw - 48px))',
        height: '68px',
        borderRadius: '18px',
        background: 'rgba(0,0,0,0.75)',
        border: '1px solid rgba(255,255,255,0.08)',
        overflow: 'hidden',
        position: 'relative',
        padding: '0 12px'
      }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(90deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 100%)',
          opacity: 0.6,
          pointerEvents: 'none'
        }} />
        <div style={{
          position: 'relative',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          fontFamily: 'Arial, sans-serif'
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            animation: 'tickerMove 25s linear infinite'
          }}>
            {renderTickerLoop(0)}
            {renderTickerLoop(1)}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          color: 'white',
          fontSize: '24px',
          fontWeight: 'bold'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!auctionData || !inviteCode) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          color: 'white',
          fontSize: '24px',
          fontWeight: 'bold',
          textAlign: 'center'
        }}>
          Invalid invite code or no auction data
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Confetti maintains full screen but content sits bottom */}
      {showConfetti && overlayEvent?.type === 'sold' && (
        <Confetti style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9500 }} />
      )}

          <div style={{
            position: 'fixed',
        bottom: 0,
            left: 0,
            width: '100vw',
        display: 'flex',
        justifyContent: 'center',
        padding: '20px 12px',
        pointerEvents: 'none',
        zIndex: 9400
      }}>
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          gap: '12px',
          width: '100%',
          pointerEvents: 'none'
              }}>
          {renderOverlayContent()}
          {renderTicker()}
            </div>
          </div>

      {/* Toast Container */}
      <ToastContainer
        position="top-center"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{
          fontSize: '14px',
          zIndex: 9600
        }}
      />

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes pulse {
          0% {
            box-shadow: 0 12px 40px rgba(0,0,0,0.4), 0 0 0 0 rgba(0,255,136,0.3);
          }
          70% {
            box-shadow: 0 12px 40px rgba(0,0,0,0.4), 0 0 0 12px rgba(0,255,136,0);
          }
          100% {
            box-shadow: 0 12px 40px rgba(0,0,0,0.4), 0 0 0 0 rgba(0,255,136,0);
          }
        }
        
        @keyframes tickerMove {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
      `}</style>
    </>
  );
};

// Unsold Display Component
const UnsoldDisplay = ({ player }) => {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      padding: '10px',
      background: 'rgba(255,0,100,0.1)',
      borderRadius: '8px',
      border: '1px solid rgba(255,0,100,0.3)'
    }}>
      <img
        src={player.img}
        alt={player.name}
        style={{
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          objectFit: 'cover',
          border: '2px solid #ff0064'
        }}
        onError={e => { e.currentTarget.src = '/placeholder.svg'; }}
      />
      <div>
        <div style={{
          fontWeight: 'bold',
          color: '#ff0064',
          fontSize: '16px'
        }}>
          {player.name} [{player.pdf_sl_no}]
        </div>
        <div style={{
          color: '#ff0064',
          fontSize: '14px'
        }}>
          Player Unsold
        </div>
      </div>
    </div>
  );
};

export default CurrentBidOverlay;
